<template>
  <b-modal
    id="modal-new-position"
    ref="modal"
    :title="'new_order_position' | i18n"
    :ok-title="'save' | i18n"
    :cancel-title="'abort' | i18n"
    ok-variant="primary"
    cancel-variant="outline-primary"
    size="xl"
    button-size="sm"
    :ok-disabled="!isFormValid"
    :busy="isLoading"
    @ok="createPosition"
    @hidden="resetModal"
  >
    <b-form>
      <div class="row">
        <div class="col-12">
          <b-table
            id="article-list"
            table-class="table-filtered"
            :fields="fields"
            :filter="filter"
            :items="getPage"
            :responsive="true"
            :busy="isLoading"
            :current-page="currentPage"
            :per-page="perPage"
            :sticky-header="true"
            :empty-text="noTableDataTitle"
            :empty-filtered-text="noTableDataTitle"
            fixed
            show-empty
          >
            <template v-slot:head(articleNumber)="row">
              {{ row.label }}
              <table-filter v-model="filter.articleNumber" :placeholder="'article_number_short' | i18n" />
            </template>
            <template v-slot:head(matchcode)="row">
              {{ row.label }}
              <table-filter v-model="filter.matchcode" :placeholder="'matchcode' | i18n" />
            </template>
            <template v-slot:head(internalName)="row">
              {{ row.label }}
              <table-filter v-model="filter.internalName" :placeholder="'designation' | i18n" />
            </template>
            <template v-slot:cell(check)="row">
              <b-form-checkbox
                :id="'checkbox__' + row.item.id"
                v-model="row.item.isSelected"
                :checked="row.item.isSelected"
                :name="'checkbox__' + row.item.id"
                @change="setSelectedArticle(row.item)"
              />
            </template>
            <template v-slot:table-busy>
              <div class="text-center">
                <b-spinner class="align-middle" />
                <strong class="ml-1">{{ 'loading' | i18n }}...</strong>
              </div>
            </template>
          </b-table>
          <b-pagination v-model="currentPage" class="table-pagination" :total-rows="totalCount" :per-page="perPage" size="sm" />
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-md-6">
          <b-form-group id="price" :label="'price' | i18n" label-for="price-input">
            <money id="price-input" v-model="formData.price" v-bind="money" class="form-control form-control-sm" />
          </b-form-group>
        </div>
        <div class="col-12 col-md-6">
          <b-form-group id="amount" :label="'amount' | i18n" label-for="amount-input">
            <b-form-input id="amount-input" v-model.number="formData.amount" type="number" size="sm" trim />
          </b-form-group>
        </div>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { BTable } from 'bootstrap-vue';
import { Money } from 'v-money';
import TableFilter from '../table/table-filter';
import { ArticleMixin, OrderMixin, NotificationMixin, ScreenSizeMixin } from '../../mixins';
import { NO_TABLE_DATA_TITLE } from '../../../../../constants';

export default {
  name: 'NewPosition',
  components: { BTable, TableFilter, Money },
  mixins: [ArticleMixin, OrderMixin, NotificationMixin, ScreenSizeMixin],
  props: {
    orderId: {
      type: String,
      default: null
    }
  },
  data: function() {
    return {
      formData: {
        price: 0,
        amount: null,
        articleNumber: null
      },
      articles: [],
      selectedArticle: null,
      fields: [
        { key: 'check', label: '', thClass: 'check' },
        { key: 'articleNumber', label: this.$translate('article_number_short'), thClass: 'article-number' },
        { key: 'matchcode', label: this.$translate('matchcode'), thClass: 'matchcode' },
        { key: 'internalName', label: this.$translate('designation'), thClass: 'designation' }
      ],
      filter: {
        articleNumber: null,
        matchcode: null,
        internalName: null
      },
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      isLoading: false,
      noTableDataTitle: this.$translate(NO_TABLE_DATA_TITLE),
      money: {
        decimal: ',',
        thousands: '.',
        suffix: ' €',
        precision: 2,
        masked: false
      }
    };
  },
  computed: {
    isFormValid: function() {
      return !!this.selectedArticle && !!this.formData.price && !!this.formData.amount;
    }
  },
  methods: {
    async getPage(ctx) {
      try {
        this.isLoading = true;
        const requestFilters = {};
        Object.keys(this.filter)
          .filter(key => !!this.filter[key])
          .forEach(key => {
            requestFilters[key] = this.filter[key];
          });

        const response = await this.articleService.getLookupPage({
          page: ctx.currentPage,
          limit: ctx.perPage,
          ...requestFilters
        });
        this.articles = response.articles.map(article => ({ ...article, isSelected: false }));
        this.totalCount = response.totalCount;
        this.isLoading = false;
        return this.articles;
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    setSelectedArticle(item) {
      this.selectedArticle = item;
      this.formData.articleNumber = item.articleNumber;
      this.articles.forEach(article => {
        if (article.id !== this.selectedArticle.id) {
          article.isSelected = false;
        }
      });
    },
    async createPosition(event) {
      event.preventDefault();
      try {
        await this.orderService.createOrderPosition(this.orderId, this.formData);
        this.showNotification({
          type: 'success',
          title: this.$translate('new_order_position'),
          message: this.$translate('new_order_position_created')
        });
        this.$refs.modal.hide();
        this.$emit('position-created');
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    resetModal() {
      this.selectedArticle = null;
      Object.keys(this.filter).forEach(key => {
        this.filter[key] = null;
      });
      this.formData.articleNumber = null;
      this.formData.price = 0;
      this.formData.amount = null;
    }
  }
};
</script>

<style scoped lang="scss">
::v-deep   .b-table-sticky-header {
  max-height: calc(100vh - 600px);
  min-height: 500px;
  width: calc(100% - 3.5px);
}

::v-deep   .table {
  min-width: 900px;

  td,
  th {
    &.check {
      width: 5%;
    }

    &.article-number {
      width: 15%;
    }

    &.matchcode {
      width: 20%;
    }

    &.designation {
      width: 60%;
    }
  }
}
</style>
